import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { useAuth } from '../../shared/auth/AuthContext';
import { formatDateTime, EMPTY } from '../../shared/utils/format';
import { Badge, Button, Input, LoadingSkeleton, Select } from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import './obligations.css';

/* ───────────────────────────────────────────────────────────────────────────
   Assistente SPED — 4 etapas (geração, validação, assinatura, transmissão).
   Consome: POST /obligations/sped (inicia, devolve SpedGeracaoDto),
   GET /obligations/sped/:id (estado/etapa), POST /obligations/sped/:id/validate
   (validacoes[]), POST /obligations/sped/:id/sign (certificadoDigital, pin?),
   POST /obligations/sped/:id/transmit ({ geracao, arquivo } — protocolo SEFAZ
   + recibo). O backend guarda `etapa` (CONTEXTO → VALIDACAO →
   GERACAO_ASSINATURA → TRANSMISSAO → CONCLUIDO); a página avança de passo
   somente quando a mutação correspondente responde.
   ─────────────────────────────────────────────────────────────────────────── */

interface ValidacaoRegistro {
  registro: string;
  descricao: string;
  status: string;
  detalhes: string;
}

interface SpedGeracao {
  id: string;
  tipo: string;
  periodoApuracao: string;
  razaoSocial: string;
  entidadeCnpj: string;
  filialCnpj: string | null;
  validacoes: ValidacaoRegistro[] | null;
  arquivoGerado: string | null;
  certificadoDigital: string | null;
  hashAssinatura: string | null;
  protocoloSefaz: string | null;
  recibo: Record<string, unknown> | null;
  dataTransmissao: string | null;
  etapa: string;
  status: string;
}

interface ArquivoTransmitido {
  id: string;
  arquivoNome: string;
  protocoloSefaz: string | null;
  recibo: Record<string, unknown> | null;
}

const TIPO_LABEL: Record<string, string> = {
  FISCAL: 'SPED Fiscal (EFD ICMS/IPI)',
  CONTABIL: 'ECD (Contábil)',
  CONTRIBUICOES: 'EFD Contribuições',
};

const VALIDACAO_VARIANT: Record<string, 'success' | 'warning' | 'error'> = {
  VALIDADO: 'success',
  ATENCAO: 'warning',
  ERRO: 'error',
};

const PASSOS = ['Contexto Fiscal', 'Validação de Dados', 'Geração e Assinatura', 'Transmissão e Recibo'];

export function SpedWizardPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [tipo, setTipo] = useState('FISCAL');
  const [periodo, setPeriodo] = useState('');
  const [filialCnpj, setFilialCnpj] = useState('');
  const [certificado, setCertificado] = useState('');
  const [pin, setPin] = useState('');
  const [spedId, setSpedId] = useState<string | null>(null);
  const [arquivo, setArquivo] = useState<ArquivoTransmitido | null>(null);
  const [passo, setPasso] = useState(0);

  const spedQuery = useQuery({
    queryKey: ['obligations', tenantId, 'sped', spedId],
    queryFn: () => apiClient.get<SpedGeracao>(`/obligations/sped/${spedId ?? ''}`),
    enabled: !!spedId,
  });
  const sped = spedQuery.data;

  const invalidateSped = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['obligations', tenantId, 'sped'] });
  };

  const startMutation = useMutation({
    mutationFn: () =>
      apiClient.post<SpedGeracao>('/obligations/sped', {
        tipo,
        periodoApuracao: periodo,
        ...(filialCnpj.trim() ? { filialCnpj: filialCnpj.replace(/\D/g, '') } : {}),
      }),
    onSuccess: (geracao) => {
      setSpedId(geracao.id);
      setPasso(1);
      addToast('Geração iniciada', 'success');
      void queryClient.invalidateQueries({ queryKey: ['obligations', tenantId, 'sped'] });
    },
    onError: (e) => addToast(e instanceof Error ? e.message : 'Erro ao iniciar geração', 'error'),
  });

  const validateMutation = useMutation({
    mutationFn: () => apiClient.post<SpedGeracao>(`/obligations/sped/${spedId ?? ''}/validate`),
    onSuccess: () => {
      setPasso(2);
      invalidateSped();
    },
    onError: (e) => addToast(e instanceof Error ? e.message : 'Erro na validação', 'error'),
  });

  const signMutation = useMutation({
    mutationFn: () =>
      apiClient.post<SpedGeracao>(`/obligations/sped/${spedId ?? ''}/sign`, {
        certificadoDigital: certificado,
        ...(pin ? { pin } : {}),
      }),
    onSuccess: () => {
      setPasso(3);
      addToast('Arquivo assinado', 'success');
      invalidateSped();
    },
    onError: (e) => addToast(e instanceof Error ? e.message : 'Erro na assinatura', 'error'),
  });

  const transmitMutation = useMutation({
    mutationFn: () => apiClient.post<{ geracao: SpedGeracao; arquivo: ArquivoTransmitido }>(`/obligations/sped/${spedId ?? ''}/transmit`),
    onSuccess: (res) => {
      setArquivo(res.arquivo);
      addToast('Transmitido com sucesso', 'success');
      invalidateSped();
    },
    onError: (e) => addToast(e instanceof Error ? e.message : 'Erro na transmissão', 'error'),
  });

  const concluido = (sped?.etapa === 'CONCLUIDO' || !!sped?.protocoloSefaz) && !transmitMutation.isPending;

  return (
    <div className="obl-page">
      <div>
        <h1 className="font-display-lg text-display-lg text-on-surface m-0">Assistente SPED</h1>
        <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
          Gere, valide, assine e transmita os arquivos SPED à SEFAZ.
        </p>
      </div>

      <div className="obl-stepper">
        <ol className="obl-stepper__list">
          {PASSOS.map((label, i) => {
            const isDone = i < passo || (i === 3 && concluido);
            const isActive = i === passo && !(i === 3 && concluido);
            return (
              <li key={label} className="obl-stepper__item">
                <div className="obl-stepper__node-wrap">
                  <div className={`obl-stepper__node${isActive ? ' is-active' : ''}${isDone ? ' is-done' : ''}`}>
                    {isDone ? (
                      <span className="material-symbols-outlined" aria-hidden="true">
                        check
                      </span>
                    ) : (
                      i + 1
                    )}
                  </div>
                  <span className={`obl-stepper__label${isActive ? ' is-active' : ''}`}>{label}</span>
                </div>
                {i < PASSOS.length - 1 && <div className={`obl-stepper__line${isDone ? ' is-done' : ''}`} />}
              </li>
            );
          })}
        </ol>
        {sped && <p className="obl-stepper__ref">{TIPO_LABEL[sped.tipo] ?? sped.tipo} · {sped.periodoApuracao} · {sped.razaoSocial}</p>}
      </div>

      <div className="obl-wizard-wrap">
        {/* Etapa 1 — Contexto fiscal */}
        {passo === 0 && (
          <div className="obl-wizard-card">
            <h2 className="obl-card__title" style={{ marginBottom: '1rem' }}>Selecione o Contexto Fiscal</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <Select
                label="Tipo de obrigação"
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
              >
                {Object.entries(TIPO_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
              <Input
                label="Período de apuração (MM/AAAA)"
                type="month"
                value={periodo}
                onChange={(e) => setPeriodo(e.target.value)}
              />
            </div>
            <div style={{ marginTop: '1rem', maxWidth: 320 }}>
              <Input
                label="CNPJ da filial (opcional)"
                value={filialCnpj}
                onChange={(e) => setFilialCnpj(e.target.value)}
                placeholder="Apenas dígitos"
                helperText="Se vazio, usa a entidade legal."
              />
            </div>
            <div className="obl-wizard-actions">
              <span />
              <div className="obl-wizard-actions__right">
                <Button
                  variant="primary"
                  disabled={!periodo || startMutation.isPending}
                  onClick={() => startMutation.mutate()}
                >
                  {startMutation.isPending ? 'Iniciando...' : 'Iniciar geração'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Etapa 2 — Validação de dados */}
        {passo === 1 && (
          <div className="obl-wizard-card">
            <h2 className="obl-card__title" style={{ marginBottom: '1rem' }}>Validação de Dados</h2>
            {validateMutation.isError && (
              <p style={{ color: '#ba1a1a', fontSize: '0.875rem' }}>{(validateMutation.error as Error).message}</p>
            )}
            <div className="obl-log">
              {sped?.validacoes && sped.validacoes.length > 0 ? (
                sped.validacoes.map((v, i) => (
                  <div key={`${v.registro}-${i}`} className="obl-log__row">
                    <span className="obl-log__time">{v.registro}</span>
                    <span className="obl-log__msg">
                      {v.descricao} — {v.detalhes}
                    </span>
                    <Badge variant={VALIDACAO_VARIANT[v.status] ?? 'neutral'} size="sm">
                      {v.status}
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="obl-log__msg">Nenhuma validação executada ainda. Clique em validar.</p>
              )}
            </div>
            <div className="obl-wizard-actions">
              <span />
              <div className="obl-wizard-actions__right">
                <Button variant="primary" disabled={validateMutation.isPending} onClick={() => validateMutation.mutate()}>
                  {validateMutation.isPending ? 'Validando...' : 'Validar dados'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Etapa 3 — Geração e assinatura */}
        {passo === 2 && (
          <div className="obl-wizard-card">
            <h2 className="obl-card__title" style={{ marginBottom: '1rem' }}>Geração e Assinatura</h2>
            <div className="obl-summary">
              <div className="obl-summary__row">
                <span className="obl-summary__key">Arquivo gerado</span>
                <span className="obl-summary__value">{sped?.arquivoGerado ?? EMPTY}</span>
              </div>
              {sped?.hashAssinatura && (
                <div className="obl-summary__row">
                  <span className="obl-summary__key">Hash SHA-256</span>
                  <span className="obl-summary__value obl-mono" style={{ wordBreak: 'break-all' }}>{sped.hashAssinatura}</span>
                </div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
              <Input
                label="Certificado digital A1"
                value={certificado}
                onChange={(e) => setCertificado(e.target.value)}
                placeholder="Ex.: Certificado A1 - Empresa XYZ"
              />
              <Input
                label="PIN (opcional)"
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
              />
            </div>
            <div className="obl-wizard-actions">
              <span />
              <div className="obl-wizard-actions__right">
                <Button variant="primary" disabled={!certificado || signMutation.isPending} onClick={() => signMutation.mutate()}>
                  {signMutation.isPending ? 'Assinando...' : 'Assinar arquivo'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Etapa 4 — Transmissão e recibo */}
        {passo === 3 && (
          <div className="obl-wizard-card">
            <h2 className="obl-card__title" style={{ marginBottom: '1rem' }}>Transmissão e Recibo</h2>
            {transmitMutation.isError && (
              <p style={{ color: '#ba1a1a', fontSize: '0.875rem' }}>{(transmitMutation.error as Error).message}</p>
            )}
            {!concluido && (
              <Button variant="primary" disabled={transmitMutation.isPending} onClick={() => transmitMutation.mutate()}>
                {transmitMutation.isPending ? 'Transmitindo...' : 'Transmitir para a SEFAZ'}
              </Button>
            )}
            {concluido && (
              <div className="obl-summary">
                <div className="obl-protocol">
                  <div className="obl-protocol__label">Protocolo SEFAZ</div>
                  <div className="obl-protocol__value">{sped?.protocoloSefaz ?? arquivo?.protocoloSefaz ?? EMPTY}</div>
                </div>
                <div className="obl-summary__row">
                  <span className="obl-summary__key">Arquivo</span>
                  <span className="obl-summary__value">{arquivo?.arquivoNome ?? sped?.arquivoGerado ?? EMPTY}</span>
                </div>
                <div className="obl-summary__row">
                  <span className="obl-summary__key">Transmitido em</span>
                  <span className="obl-summary__value">{sped?.dataTransmissao ? formatDateTime(sped.dataTransmissao) : EMPTY}</span>
                </div>
                {sped?.recibo && (
                  <div style={{ fontSize: '0.875rem' }}>
                    <span className="obl-summary__key">Recibo:</span>
                    <pre className="obl-preview">{JSON.stringify(sped.recibo, null, 2)}</pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {sped && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div className="obl-progress" style={{ flex: 1 }}>
              <div className="obl-progress__bar" style={{ width: `${((passo + (concluido ? 1 : 0)) / PASSOS.length) * 100}%` }} />
            </div>
            <span className="obl-count obl-mono">Status: {sped.status}</span>
          </div>
        )}
      </div>
      {spedQuery.isLoading && <LoadingSkeleton count={2} height="120px" />}
    </div>
  );
}